import { FC } from 'react';
import { IoAddOutline, IoGitNetworkOutline } from 'react-icons/io5';

import { CompanyProfile } from 'models';
import { RouterHelpers } from 'helpers/RouterHelpers';

import { SparseSectionPanel, useIsSparseSection } from 'components/company-profile/components/SparseSectionPanel';
import { ProfileSectionHelpers } from 'components/company-profile/helpers/ProfileSectionHelpers';
import { useReferenceDesigns } from 'components/company-profile/hooks/use-reference-designs';
import { DesignLibraryTeaser } from 'components/design-library-teaser/DesignLibraryTeaser';
import { CarouselSection } from 'components/section/CarouselSection';
import { GridSection } from 'components/section/GridSection';
import { Section, SectionProps } from 'components/section/Section';

const ReferenceDesigns: FC<
    {
        company: CompanyProfile;
        isCarousel?: boolean;
    } & SectionProps
> = ({ company, isCarousel, ...props }) => {
    const { referenceDesigns } = useReferenceDesigns(company);

    const sparsePanel = useIsSparseSection(referenceDesigns.length) && (
        <SparseSectionPanel
            key="sparse-panel"
            owner={{
                icon: <IoAddOutline />,
                title: 'Add a reference design',
                description: 'Show how your products work together in a real system.',
                cta: 'Create design',
                href: RouterHelpers.urls.profiles.edit(company.slug, 'reference-designs'),
            }}
            visitor={{
                icon: <IoGitNetworkOutline />,
                title: 'Explore more designs',
                description: 'Browse reference designs from across the design library.',
                cta: 'Browse designs',
                href: RouterHelpers.urls.designs.overview(),
            }}
        />
    );

    const Layout = isCarousel ? CarouselSection : GridSection;

    return (
        <Section title="Reference Designs" id="reference-designs" {...props}>
            {referenceDesigns.length > 0 && (
                <Layout cols={ProfileSectionHelpers.cols}>
                    {referenceDesigns.map((project) => (
                        <DesignLibraryTeaser key={project.id} project={project} variant="profile" />
                    ))}
                    {sparsePanel}
                </Layout>
            )}
        </Section>
    );
};

export { ReferenceDesigns };
